//#region redux
import { useDispatch } from "react-redux";
import { incomingInvoiceUpdatedContent } from "store/actions/incomingInvoiceActions";
import { receiptUpdatedContent } from "store/actions/receiptActions";
import { invoiceUpdatedContent } from "store/actions/invoiceActions";
import { missingDocumentUpdatedConent } from "store/actions/missingDocumentActions";
//#endregion
//#region types
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { ImageMeta } from "models/propTypes/fileViewerPropTypes";
import { InvoiceTodoExtended } from "models/propTypes/invoicePropTypes";
//#endregion
//#region components
import {
  useInvoiceService,
  useIncomingInvoiceService,
  useReceiptService,
  useMissingDocumentService,
} from "serviceHooks";
//#endregion
import { UtilsHelper } from "utils/helpers";

function usePreloadedTodoData() {
  const invoiceService = useInvoiceService();
  const incomingInvoiceService = useIncomingInvoiceService();
  const receiptService = useReceiptService();
  const missingDocumentService = useMissingDocumentService();
  const dispatch = useDispatch();

  const preLoadNext = (
    todo: WintSuperkollAPIModel.Todo,
    onPreloadSuccess?: (todo: WintSuperkollAPIModel.Todo) => void
  ) => {
    let isUpdated = false;
    switch (todo.type) {
      case WintSuperkollAPIModel.TodoType.IncomingInvoice:
        const incomingInvoice = todo.incomingInvoice;
        if (
          incomingInvoice &&
          incomingInvoice.attachments?.length &&
          UtilsHelper.isEmpty(incomingInvoice.attachments![0].data)
        ) {
          const attachment = incomingInvoice.attachments![0] as ImageMeta;

          attachment.loading = true;

          const updateAttachment = (result: string) => {
            attachment.data = result;
            attachment.loading = false;
            incomingInvoice!.attachments![0] = attachment as WintSuperkollAPIModel.IncomingInvoiceAttachment;
            dispatch(incomingInvoiceUpdatedContent(incomingInvoice!));
          };
          incomingInvoiceService
            .getAttachment(incomingInvoice!.id, incomingInvoice!.attachments[0].id)
            .then((data) => updateAttachment(data))
            .catch(() => updateAttachment(""));

          isUpdated = true;
        }

        break;
      case WintSuperkollAPIModel.TodoType.Receipt:
        const receipt = todo.receipt;
        if (receipt && receipt.attachments?.length && UtilsHelper.isEmpty(receipt.attachments![0].data)) {
          const attachment = receipt.attachments![0] as ImageMeta;

          attachment.loading = true;

          const updateAttachment = (result: string) => {
            attachment.data = result;
            attachment.loading = false;
            receipt!.attachments![0] = attachment as WintSuperkollAPIModel.ReceiptAttachment;
            dispatch(receiptUpdatedContent(receipt!));
          };
          receiptService
            .getAttachment(receipt.id, receipt.attachments[0].id)
            .then((res) => res.data ?? "")
            .then((data) => updateAttachment(data))
            .catch(() => updateAttachment(""));

          isUpdated = true;
        }
        break;
      case WintSuperkollAPIModel.TodoType.Invoice:
        const invoice = todo.invoice;
        const extendedInvoice = invoice! as unknown as InvoiceTodoExtended;
        if (invoice && UtilsHelper.isEmpty(extendedInvoice.preview)) {
          extendedInvoice.previewLoading = true;

          const updateAttachment = (result: string) => {
            extendedInvoice.preview = result;
            extendedInvoice.previewLoading = false;
            dispatch(invoiceUpdatedContent(extendedInvoice!));
          };

          invoiceService
            .getInvoicePDFPreview(invoice!.id)
            .then((res) => res.data as unknown as string)
            .then((data) => updateAttachment(data))
            .catch(() => updateAttachment(""));

          isUpdated = true;
        }
        break;
      case WintSuperkollAPIModel.TodoType.MissingDocument:
        const missingDocument = todo.missingDocument;
        if (missingDocument && missingDocument.attachments?.length) {
          const attachment = missingDocument!.attachments![0] as ImageMeta;
          if (UtilsHelper.isEmpty(attachment.data)) {
            attachment.loading = true;
            const updateAttachment = (result: string) => {
              attachment.data = result;
              attachment.loading = false;
              missingDocument!.attachments![0] = attachment as WintSuperkollAPIModel.MissingDocumentAttachment;
              dispatch(missingDocumentUpdatedConent(missingDocument!));
            };

            missingDocumentService
              .getAttachment(missingDocument.id, attachment.id as number)
              .then((data) => updateAttachment(data))
              .catch(() => updateAttachment(""));

            isUpdated = true;
          }
        }
        break;
    }
    if (onPreloadSuccess && isUpdated) onPreloadSuccess(todo);
  };

  return { preLoadNext };
}

export default usePreloadedTodoData;
